/**
 * ScoringTab — Module de scoring agents paramétrable (Admin RH).
 *
 * 3 sous-vues :
 *   1. Liste des agents + accès édition pondérations & saisie mensuelle
 *   2. Editeur de barème (par défaut OU par agent OU Cyrille)
 *   3. Saisie mensuelle + sous-barèmes Titulaire/Manager direct
 *   + Modale Lexique consultable
 *   + Tableau Historique des modifications
 *
 * Référence métier : `scoring_agents_final_avec_prompts_360.xlsx`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Settings, Edit3, BookOpen, RotateCcw, Save, ArrowLeft, History,
  CheckCircle2, AlertCircle, ClipboardEdit, Crown, User as UserIcon,
  LayoutDashboard,
} from 'lucide-react';
import { Button } from '../../ui/button';
import ScoringDashboard from './ScoringDashboard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

// Détection rôle côté client — l'utilisateur courant est stocké au login.
const getCurrentRole = () => {
  try {
    const u = JSON.parse(localStorage.getItem('dermascan_user') || '{}');
    if (u.is_admin || u.role === 'admin') return 'admin';
    if (u.role === 'manager') return 'manager';
    return u.role || 'agent';
  } catch { return 'agent'; }
};
const getCurrentUserId = () => {
  try { return JSON.parse(localStorage.getItem('dermascan_user') || '{}').id; }
  catch { return null; }
};

const AGENT_AXIS_LABELS = {
  caisse: 'Caisse',
  ponctualite: 'Ponctualité / assiduité',
  sadaskincare: 'SADASkinCare',
  sadahealth: 'SADAHealth',
  sadarayon: 'SADARayon',
  sadagas: 'SADAGAS',
  polyvalence: 'Polyvalence / contribution',
  titulaire: 'Appréciation du titulaire',
  manager_direct: 'Appréciation du manager direct',
};
const CYRILLE_AXIS_LABELS = {
  resultats_equipe: 'Résultats de l\'équipe',
  perf_commerciale_perso: 'Performance commerciale perso',
  discipline_equipe: 'Discipline opérationnelle équipe',
  disponibilite_stock: 'Disponibilité produits / stock',
  titulaire: 'Appréciation du titulaire',
};

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const StatusBadge = ({ score }) => {
  if (score == null) return <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-100 text-zinc-500">—</span>;
  const cls = score >= 80 ? 'bg-emerald-100 text-emerald-700'
    : score >= 60 ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700';
  const label = score >= 80 ? 'Performant' : score >= 60 ? 'À suivre' : 'À accompagner';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{score.toFixed(1)} · {label}</span>;
};

// ─────────────── Editeur de barème ───────────────
const WeightsEditor = ({ axes, labels, initial, onSave, onReset, title, subtitle, allowReset }) => {
  const [weights, setWeights] = useState(initial || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setWeights(initial || {}); }, [initial]);

  const total = useMemo(
    () => Object.values(weights).reduce((s, v) => s + (parseFloat(v) || 0), 0),
    [weights]
  );
  const isValid = Math.abs(total - 100) < 0.01;

  const setAxis = (axe, v) => {
    const n = Math.max(0, Math.min(100, parseFloat(v) || 0));
    setWeights((w) => ({ ...w, [axe]: n }));
  };

  const submit = async () => {
    if (!isValid) { toast.error(`Le total doit être 100% (actuel ${total.toFixed(1)}%)`); return; }
    setSaving(true);
    try { await onSave(weights); toast.success('Barème enregistré'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur enregistrement'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-4" data-testid="weights-editor">
      <div>
        <h3 className="text-lg font-semibold text-zinc-900">{title}</h3>
        {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
      </div>

      <div className="space-y-2">
        {axes.map((a) => (
          <div key={a} className="grid grid-cols-12 gap-2 items-center">
            <label className="col-span-7 text-sm text-zinc-700" htmlFor={`w-${a}`}>{labels[a] || a}</label>
            <div className="col-span-3">
              <input
                id={`w-${a}`}
                type="number"
                min="0"
                max="100"
                step="1"
                value={weights[a] ?? 0}
                onChange={(e) => setAxis(a, e.target.value)}
                className="w-full h-10 px-2 text-right rounded border border-zinc-300 focus:ring-2 focus:ring-[#D97706]"
                data-testid={`weight-input-${a}`}
              />
            </div>
            <div className="col-span-2 text-xs text-zinc-400">%</div>
          </div>
        ))}
      </div>

      <div className={`flex items-center justify-between p-3 rounded-lg ${isValid ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
        <span className={`text-sm font-semibold ${isValid ? 'text-emerald-700' : 'text-red-700'}`}>
          {isValid ? '✓ Total = 100%' : `⚠ Total = ${total.toFixed(1)}% (doit être 100%)`}
        </span>
        <div className="flex items-center gap-2">
          {allowReset && (
            <Button variant="outline" size="sm" onClick={onReset} className="h-9" data-testid="weight-reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
          )}
          <Button onClick={submit} disabled={!isValid || saving} className="h-9 bg-[#D97706] hover:bg-[#b45309] text-white" data-testid="weight-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─────────────── Saisie mensuelle ───────────────
const MonthlyEntryEditor = ({ agent, axes, labels, lexicon }) => {
  const role = getCurrentRole();
  const isManagerOnly = role === 'manager';
  const [period, setPeriod] = useState(currentPeriod());
  const [notes, setNotes] = useState({});
  const [subT, setSubT] = useState({});
  const [subM, setSubM] = useState({});
  const [commercial, setCommercial] = useState({});
  const [comment, setComment] = useState('');
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const isCyrille = agent.is_cyrille;
  const subT_criteria = lexicon?.subcriteria?.titulaire || [];
  const subM_criteria = lexicon?.subcriteria?.manager_direct || [];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const e = await axios.get(`${API}/agent-scoring/entries?agent_id=${agent.id}&period=${period}`, { headers: auth() });
      setNotes(e.data?.notes || {});
      setSubT(e.data?.titulaire_sub || {});
      setSubM(e.data?.manager_direct_sub || {});
      setCommercial(e.data?.cyrille_commercial || {});
      setComment(e.data?.comment || '');
      const s = await axios.get(`${API}/agent-scoring/score/${agent.id}?period=${period}`, { headers: auth() });
      setScore(s.data);
    } catch { /* empty */ }
    finally { setLoading(false); }
  }, [agent.id, period]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      // Strip null/undefined values pour éviter une validation 422 backend
      // (un champ vidé côté UI = "non saisi", pas une note 0).
      const stripNull = (obj) => Object.fromEntries(
        Object.entries(obj || {}).filter(([, v]) => v !== null && v !== undefined && !Number.isNaN(v))
      );
      const payload = {
        agent_id: agent.id,
        period,
        notes: stripNull(notes),
        titulaire_sub: stripNull(subT),
        manager_direct_sub: stripNull(subM),
        cyrille_commercial: isCyrille ? stripNull(commercial) : null,
        comment: comment || null,
      };
      await axios.put(`${API}/agent-scoring/entries`, payload, { headers: auth() });
      toast.success('Saisie enregistrée');
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      const text = typeof msg === 'string' ? msg : (Array.isArray(msg) ? msg.map(m => m.msg || JSON.stringify(m)).join(' · ') : 'Erreur lors de l\'enregistrement');
      toast.error(text);
    }
    finally { setSaving(false); }
  };

  const NoteInput = ({ value, onChange, testid }) => (
    <input type="number" min="0" max="10" step="0.5" value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Math.max(0, Math.min(10, parseFloat(e.target.value))))}
      className="w-full h-10 px-2 text-center rounded border border-zinc-300 focus:ring-2 focus:ring-[#D97706]"
      placeholder="—" data-testid={testid}
    />
  );

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-5" data-testid="monthly-entry-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-zinc-900">{agent.name}{isCyrille && <Crown className="inline w-4 h-4 ml-1 text-amber-500" />}</h3>
          <p className="text-xs text-zinc-500">Saisie mensuelle · notes /10</p>
        </div>
        <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
          className="h-10 px-3 rounded border border-zinc-300" data-testid="period-input" />
      </div>

      {loading ? <p className="text-sm text-zinc-400">Chargement…</p> : (
        <>
          {isManagerOnly && (
            <div className="bg-blue-50 border border-blue-200 rounded p-2.5 text-xs text-blue-800 mb-1" data-testid="manager-only-banner">
              <strong>Mode Manager (lecture seule sur les barèmes)</strong> — vous saisissez uniquement les sous-critères « Appréciation du manager direct ». Les autres axes sont gérés par l'admin.
            </div>
          )}
          <div className={`space-y-2 ${isManagerOnly ? 'opacity-60 pointer-events-none' : ''}`}>
            <h4 className="text-sm font-semibold text-zinc-700">
              Notes par axe {isManagerOnly && <span className="text-xs font-normal text-zinc-500">(lecture seule)</span>}
            </h4>
            {axes.filter((a) => a !== 'titulaire' && a !== 'manager_direct').map((a) => (
              <div key={a} className="grid grid-cols-12 gap-2 items-center">
                <label className="col-span-8 text-sm text-zinc-600">{labels[a]}</label>
                <div className="col-span-4"><NoteInput value={notes[a]} onChange={(v) => setNotes((n) => ({ ...n, [a]: v }))} testid={`note-${a}`} /></div>
              </div>
            ))}
          </div>

          {/* Sous-barème Titulaire (commun) — masqué pour manager */}
          {axes.includes('titulaire') && !isManagerOnly && (
            <div className="space-y-2 border-t border-zinc-100 pt-3">
              <h4 className="text-sm font-semibold text-zinc-700">Sous-barème · Appréciation du titulaire</h4>
              <p className="text-xs text-zinc-500">La note de l'axe sera calculée comme moyenne des sous-critères.</p>
              {subT_criteria.map((c) => (
                <div key={c.key} className="grid grid-cols-12 gap-2 items-center">
                  <label className="col-span-8 text-sm text-zinc-600" title={c.def}>{c.label}</label>
                  <div className="col-span-4"><NoteInput value={subT[c.key]} onChange={(v) => setSubT((s) => ({ ...s, [c.key]: v }))} testid={`subt-${c.key}`} /></div>
                </div>
              ))}
            </div>
          )}

          {/* Sous-barème Manager direct (autorisé pour manager — sa seule zone d'écriture) */}
          {axes.includes('manager_direct') && (
            <div className="space-y-2 border-t border-zinc-100 pt-3 bg-blue-50/40 p-3 rounded">
              <h4 className="text-sm font-semibold text-blue-900">
                Sous-barème · Appréciation du manager direct
                {isManagerOnly && <span className="ml-2 text-xs font-normal text-blue-700">✏️ Votre zone de saisie</span>}
              </h4>
              {subM_criteria.map((c) => (
                <div key={c.key} className="grid grid-cols-12 gap-2 items-center">
                  <label className="col-span-8 text-sm text-zinc-700" title={c.def}>{c.label}</label>
                  <div className="col-span-4"><NoteInput value={subM[c.key]} onChange={(v) => setSubM((s) => ({ ...s, [c.key]: v }))} testid={`subm-${c.key}`} /></div>
                </div>
              ))}
            </div>
          )}

          {/* Bloc commercial Cyrille — admin only */}
          {isCyrille && !isManagerOnly && (
            <div className="space-y-2 border-t border-zinc-100 pt-3 bg-amber-50/40 p-3 rounded">
              <h4 className="text-sm font-semibold text-amber-800">Performance commerciale personnelle Cyrille</h4>
              <p className="text-xs text-zinc-500">Objectif manager spécifique — distinct de la logique Sales/SkinCare des autres agents.</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-zinc-500">CA personnel — Réalisé</label>
                  <input type="number" value={commercial.ca_realise ?? ''} onChange={(e) => setCommercial((c) => ({ ...c, ca_realise: parseFloat(e.target.value) || 0 }))} className="w-full h-10 px-2 rounded border border-zinc-300" placeholder="FCFA" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500">CA personnel — Objectif</label>
                  <input type="number" value={commercial.ca_objectif ?? ''} onChange={(e) => setCommercial((c) => ({ ...c, ca_objectif: parseFloat(e.target.value) || 0 }))} className="w-full h-10 px-2 rounded border border-zinc-300" placeholder="FCFA" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Nb ventes — Réalisé</label>
                  <input type="number" value={commercial.nb_ventes_realise ?? ''} onChange={(e) => setCommercial((c) => ({ ...c, nb_ventes_realise: parseFloat(e.target.value) || 0 }))} className="w-full h-10 px-2 rounded border border-zinc-300" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Nb ventes — Objectif</label>
                  <input type="number" value={commercial.nb_ventes_objectif ?? ''} onChange={(e) => setCommercial((c) => ({ ...c, nb_ventes_objectif: parseFloat(e.target.value) || 0 }))} className="w-full h-10 px-2 rounded border border-zinc-300" />
                </div>
              </div>
              <p className="text-xs text-amber-700 mt-1">Barème commercial Cyrille : CA 60% · Nb ventes 25% · Qualité conversion 15%.</p>
            </div>
          )}

          {/* Commentaire */}
          <div>
            <label className="text-xs text-zinc-500">Commentaire (optionnel)</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)}
              className="w-full p-2 rounded border border-zinc-300 text-sm" rows={2} placeholder="Notes contextuelles…" />
          </div>

          <div className="flex items-center justify-between border-t border-zinc-100 pt-3">
            <div className="text-sm">
              <p className="text-zinc-500">Score calculé</p>
              <p className="text-2xl font-bold text-zinc-900">
                {score?.score_normalized != null ? `${score.score_normalized}/100` : '—'}
                {score && <StatusBadge score={score.score_normalized} />}
              </p>
              {score?.missing_axes?.length > 0 && (
                <p className="text-xs text-zinc-400">Axes non saisis : {score.missing_axes.map((a) => labels[a] || a).join(', ')}</p>
              )}
            </div>
            <Button onClick={save} disabled={saving} className="h-10 bg-[#D97706] hover:bg-[#b45309] text-white" data-testid="entry-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────── Modale Lexique ───────────────
const LexiconModal = ({ open, onClose, lexicon, profile }) => {
  if (!open) return null;
  const block = lexicon?.[profile] || {};

  const downloadLexicon = async (format) => {
    try {
      const r = await axios.get(`${API}/agent-scoring/lexicon/download?format=${format}`, {
        headers: auth(),
        responseType: 'blob',
      });
      const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `lexique_scoring_pharmacie_sada.${format}`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success(`Lexique ${format.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error('Erreur téléchargement');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="lexicon-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-lg font-bold text-zinc-900">
            <BookOpen className="inline w-5 h-5 mr-1 text-[#D97706]" /> Lexique — {profile === 'cyrille' ? 'Manager Cyrille' : 'Agent'}
          </h3>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => downloadLexicon('pdf')} className="h-8 border-red-200 text-red-700 hover:bg-red-50" data-testid="lexicon-download-pdf">
              📄 PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => downloadLexicon('docx')} className="h-8 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="lexicon-download-docx">
              📝 Word
            </Button>
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 text-xl leading-none px-2" data-testid="lexicon-close">✕</button>
          </div>
        </div>
        <div className="space-y-3">
          {Object.entries(block).map(([k, v]) => (
            <div key={k} className="border-l-4 border-[#D97706] pl-3 py-1">
              <h4 className="font-semibold text-zinc-900 text-sm">{v.label}</h4>
              <p className="text-xs text-zinc-600 mt-0.5">{v.def}</p>
              {v.ref && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mt-1.5 text-[11px]">
                  {Object.entries(v.ref).map(([range, text]) => (
                    <div key={range} className="bg-zinc-50 rounded p-1.5">
                      <strong className="text-zinc-700">{range}</strong>
                      <p className="text-zinc-500">{text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-400 mt-4 text-center">Échelle : 0-3 insuffisant · 4-5 fragile · 6-7 correct · 8-9 bon · 10 excellent.</p>
      </div>
    </div>
  );
};

// ─────────────── Historique des modifications ───────────────
const AuditPanel = ({ visible }) => {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    if (!visible) return;
    axios.get(`${API}/agent-scoring/audit?limit=30`, { headers: auth() })
      .then((r) => setLogs(r.data || []))
      .catch(() => {});
  }, [visible]);
  if (!visible) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="scoring-audit">
      <h3 className="text-lg font-semibold mb-3"><History className="inline w-5 h-5 mr-1 text-zinc-500" /> Historique des modifications</h3>
      {!logs.length ? <p className="text-sm text-zinc-400">Aucune modification enregistrée.</p> : (
        <div className="space-y-2 max-h-96 overflow-y-auto text-xs">
          {logs.map((l) => (
            <div key={l.id} className="border-l-4 border-zinc-200 pl-3 py-1">
              <p><strong>{l.actor_name}</strong> · {l.action} · {l.target}</p>
              <p className="text-zinc-400">{new Date(l.at).toLocaleString('fr-FR')}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────── Composant principal ───────────────
const ScoringTab = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('dashboard'); // dashboard | list | weights | entry | default | cyrille | audit
  const [currentAgent, setCurrentAgent] = useState(null);
  const [defaultW, setDefaultW] = useState(null);
  const [cyrilleW, setCyrilleW] = useState(null);
  const [agentW, setAgentW] = useState(null);
  const [lexicon, setLexicon] = useState(null);
  const [lexOpen, setLexOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [a, d, c, lex] = await Promise.all([
        axios.get(`${API}/agent-scoring/agents`, { headers: auth() }),
        axios.get(`${API}/agent-scoring/weights/default`, { headers: auth() }),
        axios.get(`${API}/agent-scoring/weights/cyrille`, { headers: auth() }),
        axios.get(`${API}/agent-scoring/lexicon`, { headers: auth() }),
      ]);
      setAgents(a.data || []);
      setDefaultW(d.data?.weights);
      setCyrilleW(c.data?.weights);
      setLexicon(lex.data);
    } catch (e) { toast.error('Erreur chargement scoring'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const openAgentWeights = async (agent) => {
    setCurrentAgent(agent);
    const r = await axios.get(`${API}/agent-scoring/weights/agent/${agent.id}`, { headers: auth() });
    setAgentW(r.data?.weights);
    setView('weights');
  };

  const saveAgentWeights = async (w) => {
    await axios.put(`${API}/agent-scoring/weights/agent/${currentAgent.id}`, { weights: w }, { headers: auth() });
    await reload();
  };
  const resetAgentWeights = async () => {
    if (!window.confirm('Réinitialiser le barème de cet agent au barème par défaut ?')) return;
    await axios.delete(`${API}/agent-scoring/weights/agent/${currentAgent.id}`, { headers: auth() });
    toast.success('Barème réinitialisé');
    await reload();
    setView('list');
  };

  if (loading) return <p className="text-sm text-zinc-400 p-4">Chargement…</p>;

  // ─── Vue Dashboard (par défaut) ───
  if (view === 'dashboard') {
    const role = getCurrentRole();
    const isAdmin = role === 'admin';
    return (
      <div className="space-y-4">
        <ScoringDashboard onOpenAgent={(a) => { setCurrentAgent(a); setView('entry'); }} />
        <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-zinc-200">
          <span className="text-xs text-zinc-500">Configuration :</span>
          <Button variant="outline" size="sm" onClick={() => setView('list')} data-testid="dashboard-to-list"><Settings className="w-4 h-4 mr-1" /> Saisie & barèmes</Button>
          <Button variant="outline" size="sm" onClick={() => setLexOpen(true)} data-testid="dashboard-show-lexicon"><BookOpen className="w-4 h-4 mr-1" /> Lexique</Button>
          {/* Barèmes : admin uniquement */}
          {isAdmin && <Button variant="outline" size="sm" onClick={() => setView('default')} data-testid="dashboard-edit-default"><Settings className="w-4 h-4 mr-1" /> Barème par défaut</Button>}
          {isAdmin && <Button variant="outline" size="sm" onClick={() => setView('cyrille')} data-testid="dashboard-edit-cyrille"><Crown className="w-4 h-4 mr-1" /> Barème Cyrille</Button>}
          {isAdmin && <Button variant="outline" size="sm" onClick={() => setView('audit')} data-testid="dashboard-show-audit"><History className="w-4 h-4 mr-1" /> Historique</Button>}
        </div>
        <LexiconModal open={lexOpen} onClose={() => setLexOpen(false)} lexicon={lexicon} profile="agent" />
      </div>
    );
  }

  // ─── Vues ───
  if (view === 'default') {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => setView('list')} data-testid="scoring-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
        <WeightsEditor axes={Object.keys(AGENT_AXIS_LABELS)} labels={AGENT_AXIS_LABELS} initial={defaultW}
          title="Barème par défaut — agents non managers" subtitle="Appliqué automatiquement à tout agent sans pondération personnalisée."
          onSave={async (w) => { await axios.put(`${API}/agent-scoring/weights/default`, { weights: w }, { headers: auth() }); await reload(); }}
          allowReset={false}
        />
      </div>
    );
  }
  if (view === 'cyrille') {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => setView('list')} data-testid="scoring-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
        <WeightsEditor axes={Object.keys(CYRILLE_AXIS_LABELS)} labels={CYRILLE_AXIS_LABELS} initial={cyrilleW}
          title="Barème spécifique — Cyrille (Manager)" subtitle="5 axes managériaux. Distinct du barème agent."
          onSave={async (w) => { await axios.put(`${API}/agent-scoring/weights/cyrille`, { weights: w }, { headers: auth() }); await reload(); }}
          allowReset={false}
        />
      </div>
    );
  }
  if (view === 'weights' && currentAgent) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => setView('list')} data-testid="scoring-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
        <WeightsEditor axes={Object.keys(AGENT_AXIS_LABELS)} labels={AGENT_AXIS_LABELS} initial={agentW}
          title={`Barème personnalisé — ${currentAgent.name}`} subtitle="Override individuel. Total obligatoire = 100%."
          onSave={saveAgentWeights} onReset={resetAgentWeights} allowReset={currentAgent.has_custom_weights}
        />
      </div>
    );
  }
  if (view === 'entry' && currentAgent) {
    const axes = currentAgent.is_cyrille ? Object.keys(CYRILLE_AXIS_LABELS) : Object.keys(AGENT_AXIS_LABELS);
    const labels = currentAgent.is_cyrille ? CYRILLE_AXIS_LABELS : AGENT_AXIS_LABELS;
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setView('list')} data-testid="scoring-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
          <Button variant="outline" size="sm" onClick={() => setLexOpen(true)} data-testid="scoring-lexicon-btn"><BookOpen className="w-4 h-4 mr-1" /> Lexique</Button>
        </div>
        <MonthlyEntryEditor agent={currentAgent} axes={axes} labels={labels} lexicon={lexicon} />
        <LexiconModal open={lexOpen} onClose={() => setLexOpen(false)} lexicon={lexicon} profile={currentAgent.is_cyrille ? 'cyrille' : 'agent'} />
      </div>
    );
  }
  if (view === 'audit') {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => setView('list')} data-testid="scoring-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
        <AuditPanel visible />
      </div>
    );
  }

  // ─── Vue liste (configuration / saisie) ───
  const role = getCurrentRole();
  const isAdmin = role === 'admin';
  const currentUserId = getCurrentUserId();
  // Pour le manager (Cyrille), on exclut sa propre ligne ET les comptes de test.
  // Pour l'admin, seuls les comptes de test sont exclus.
  const realAgents = agents.filter((a) => {
    if (a.name?.includes('Test') || a.name?.includes('Nouvel')) return false;
    if (role === 'manager' && a.id === currentUserId) return false;
    return true;
  });
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setView('dashboard')} data-testid="list-back-dashboard"><LayoutDashboard className="w-4 h-4 mr-1" /> Dashboard</Button>
          <div>
            <h2 className="text-xl font-bold text-zinc-900">{isAdmin ? 'Saisie & Barèmes' : 'Saisie manager'}</h2>
            <p className="text-sm text-zinc-500">
              {isAdmin
                ? 'Notation mensuelle par agent + paramétrage des pondérations.'
                : 'Saisie mensuelle de l\'appréciation manager direct uniquement. Les barèmes sont en lecture seule.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setLexOpen(true)} data-testid="scoring-show-lexicon"><BookOpen className="w-4 h-4 mr-1" /> Lexique</Button>
          {/* Boutons d'administration des barèmes : ADMIN UNIQUEMENT */}
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={() => setView('audit')} data-testid="scoring-show-audit"><History className="w-4 h-4 mr-1" /> Historique</Button>
              <Button variant="outline" size="sm" onClick={() => setView('default')} className="bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100" data-testid="scoring-edit-default"><Settings className="w-4 h-4 mr-1" /> Barème par défaut</Button>
              <Button variant="outline" size="sm" onClick={() => setView('cyrille')} className="bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100" data-testid="scoring-edit-cyrille"><Crown className="w-4 h-4 mr-1" /> Barème Cyrille</Button>
            </>
          )}
        </div>
      </div>

      {!isAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900" data-testid="manager-info-banner">
          <p className="font-semibold mb-1">Mode Manager · accès restreint</p>
          <ul className="text-xs text-blue-800 list-disc list-inside space-y-0.5">
            <li>Vous pouvez saisir uniquement la <strong>note « Appréciation manager direct »</strong> pour les agents de votre équipe.</li>
            <li>Les barèmes (pondérations) sont gérés par l'administrateur — non modifiables ici.</li>
            <li>Les notes administrateur (titulaire, axes globaux, bloc commercial) sont en lecture seule.</li>
          </ul>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200 text-xs uppercase text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">Agent</th>
              <th className="px-3 py-2 text-left">Type barème</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {realAgents.map((a) => (
              <tr key={a.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                <td className="px-3 py-2 font-medium text-zinc-900">
                  {a.is_cyrille ? <Crown className="inline w-3.5 h-3.5 mr-1 text-amber-500" /> : <UserIcon className="inline w-3.5 h-3.5 mr-1 text-zinc-400" />}
                  {a.name}
                  <span className="text-xs text-zinc-400 ml-1.5">({a.role})</span>
                </td>
                <td className="px-3 py-2">
                  {a.is_cyrille ? (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 font-semibold">Manager · Cyrille</span>
                  ) : a.has_custom_weights ? (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700 font-semibold">Personnalisé</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-100 text-zinc-600">Par défaut</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    {!a.is_cyrille && isAdmin && (
                      <Button size="sm" variant="outline" onClick={() => openAgentWeights(a)} className="h-8" data-testid={`agent-weights-${a.id}`}>
                        <Edit3 className="w-3.5 h-3.5 mr-1" /> Barème
                      </Button>
                    )}
                    {/* Cyrille (manager) ne peut pas se noter lui-même */}
                    {!(role === 'manager' && a.id === currentUserId) && (
                      <Button size="sm" onClick={() => { setCurrentAgent(a); setView('entry'); }} className="h-8 bg-[#D97706] hover:bg-[#b45309] text-white" data-testid={`agent-entry-${a.id}`}>
                        <ClipboardEdit className="w-3.5 h-3.5 mr-1" /> Saisir notes
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <LexiconModal open={lexOpen} onClose={() => setLexOpen(false)} lexicon={lexicon} profile="agent" />
    </div>
  );
};

export default ScoringTab;
